import { KeyboardEvent, MouseEvent, useCallback, useState } from 'react';
import { useKeyboardShortcut } from 'src/hooks';
import { hasTriggeredExtraClickFromKeyUpEvent } from 'src/apps/LearningSession/utils/hasTriggeredExtraClickFromKeyUpEvent';
import type { NextActionState } from 'src/apps/LearningSession/Common/NextActionButton';
import type { LearningSessionAction, LearningSessionCardState } from './learningSessionReducer';

const NEXT_ACTION_THROTTLE_MS = 500;

export type NextActionMethod = 'pointer' | 'keyboard' | 'swipe';

type UseNextActionArgs = {
  card: NonNullable<LearningSessionCardState['card']>;
  learningSessionDispatch: (action: LearningSessionAction) => void;
  onFeedbackFinished: () => void;
  progressSession: () => void;
  onTriggered?: (method: NextActionMethod, state: NextActionState) => void;
};

const getNextAction = ({
  card,
  learningSessionDispatch,
  onFeedbackFinished,
  progressSession,
}: UseNextActionArgs): { state: NextActionState; action: () => void } => {
  const { responseState, userAnswer, data } = card;

  switch (data.kind) {
    case 'Test':
      if (responseState === 'Unanswered') {
        if (userAnswer === '') {
          return {
            state: 'Unanswered',
            action: () => learningSessionDispatch({ type: 'SKIP_TEST' }),
          };
        }
        return {
          state: 'CheckAnswer',
          action: () => learningSessionDispatch({ type: 'ANSWER_TEST', answer: userAnswer }),
        };
      }
      /* istanbul ignore if: to be removed in WEBSITE-2541 */
      if (responseState === 'Selected') {
        return {
          state: responseState,
          action: () => learningSessionDispatch({ type: 'ANSWER_TEST', answer: userAnswer }),
        };
      }
      return { state: responseState, action: onFeedbackFinished };
    case 'Presentation':
      return { state: 'Viewed', action: progressSession };
    /* istanbul ignore next: internal consistency check */
    default:
      throw Error('Unknown cardData kind');
  }
};

export const useNextAction = (args: UseNextActionArgs) => {
  const { state, action } = getNextAction(args);
  const { onTriggered } = args;
  const [isNextBlocked, setIsNextBlocked] = useState(false);

  const triggerNextAction = useCallback(
    (method: NextActionMethod) => {
      if (isNextBlocked) return;
      onTriggered?.(method, state);
      action();
      setIsNextBlocked(true);
      setTimeout(() => {
        setIsNextBlocked(false);
      }, NEXT_ACTION_THROTTLE_MS);
    },
    [isNextBlocked, action, onTriggered, state],
  );

  useKeyboardShortcut('enter', () => triggerNextAction('keyboard'));

  const onKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event?.key === 'Enter' || event?.key === ' ') {
        triggerNextAction('keyboard');
      }
    },
    [triggerNextAction],
  );

  const onClick = (event?: MouseEvent) => {
    if (hasTriggeredExtraClickFromKeyUpEvent(event)) return;
    triggerNextAction('pointer');
  };

  return { nextActionState: state, onClick, onKeyUp, triggerNextAction };
};
